/**
 * Knowledge · Guide Viewer — the reading/review surface (parity target:
 * curator's ComponentReview). Master-detail: the numbered guide list on the
 * left, the selected guide rendered as real prose on the right, with
 * Edit / Regenerate / Archive / Approve & publish.
 *
 * Built for NON-TECHNICAL reviewers: reading first, one obvious Edit button,
 * WYSIWYG editing (never raw markdown — and no source toggle; see
 * RichTextEditor), plain-language statuses and
 * confirmations. Deep-linkable per guide (/review/:itemId) so "look at this
 * one" is a URL someone can send.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Input,
  Modal,
  Skeleton,
  StatusIndicator,
} from '@servicecompressionllc/sc-design-system';
import { useMe } from '../../hooks/useMe.js';
import { canWriteKnowledge } from '../../lib/knowledgeAccess.js';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { MarkdownViewer } from '../../components/MarkdownViewer.js';
import { RichTextEditor } from '../../components/RichTextEditor.js';
import {
  knowledgeApi,
  type GenerationRun,
  type KnowledgeItem,
  type KnowledgeItemStatus,
} from './api.js';
import { CurateList } from './CurateList.js';
import { SubjectDetailsTab } from './SubjectDetailsTab.js';
import { GuideHistoryPanel } from './GuideHistoryPanel.js';

/** Plain-language statuses — a reviewer shouldn't need to know enum names. */
const STATUS_LABEL: Record<
  KnowledgeItemStatus,
  { text: string; variant: 'success' | 'warning' | 'info' | 'danger' | 'neutral' }
> = {
  PUBLISHED: { text: 'published', variant: 'success' },
  DRAFT: { text: 'needs review', variant: 'warning' },
  GENERATING: { text: 'being written…', variant: 'info' },
  GENERATION_FAILED: { text: 'failed', variant: 'danger' },
  ARCHIVED: { text: 'archived', variant: 'neutral' },
};

const GuideListEntry = ({
  item,
  selected,
  onSelect,
}: {
  item: KnowledgeItem;
  selected: boolean;
  onSelect: () => void;
}) => {
  const status = STATUS_LABEL[item.status];
  return (
    <button
      type="button"
      onClick={onSelect}
      // Selection is an accent bar + ground, not coloured text: a wrapped title
      // in link-blue reads as a paragraph of links. The transparent border on
      // unselected rows keeps the text from shifting when selection moves.
      className={`w-full border-b border-outline border-l-2 p-3 text-left text-sm last:border-b-0 hover:bg-surface-hover ${
        selected ? 'border-l-primary bg-surface-hover' : 'border-l-transparent'
      }`}
      aria-current={selected ? 'true' : undefined}
    >
      <span className="mr-2 text-xs text-ink-subtle">{item.ordinal}.</span>
      <span className={selected ? 'font-medium text-ink' : 'text-ink'}>{item.title}</span>
      <span className="mt-1 block">
        <Badge variant={status.variant}>{status.text}</Badge>
      </span>
    </button>
  );
};

/** One wording for each stage, so the waiting panel and the progress strip can
 *  never describe the same run differently. */
const runCopy = (run: GenerationRun, itemNoun: string) => {
  const noun = itemNoun.toLowerCase();
  return run.kind === 'LIST'
    ? {
        heading: `Working out which ${noun}s to cover`,
        detail:
          'One AI proposes the list from your documents and the category briefing, a second reviews it, then the first revises. You get to edit the result before anything is written.',
        eta: 'usually a minute or two',
      }
    : {
        heading: run.kind === 'ITEM' ? `Rewriting this ${noun}` : `Writing the ${noun}s`,
        detail:
          'Each one is drafted, reviewed against your documents, then revised. They arrive as drafts for you to approve — you can carry on reading while it works.',
        eta: 'a few minutes for a long list',
      };
};

/** "4 of 27" plus the bar. Absent until the worker has created the run's tasks,
 *  which is why a queued run shows the waiting line instead. */
const RunProgressBar = ({ done, total }: { done: number; total: number }) => {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div
      className="mt-3 h-1.5 overflow-hidden rounded-full bg-surface-hover"
      role="progressbar"
      aria-valuenow={done}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-label="Guides written"
    >
      <div className="h-full rounded-full bg-info transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
};

const RunStatusLine = ({ run }: { run: GenerationRun }) => (
  <>
    {run.progress
      ? `${run.progress.done} of ${run.progress.total} written`
      : run.status === 'PENDING'
        ? 'Waiting for a free worker…'
        : 'Starting…'}
    {run.progress && run.progress.failed > 0 && ` · ${run.progress.failed} couldn’t be written`}
  </>
);

/**
 * What an in-flight run looks like while you wait. Generation is minutes, not
 * milliseconds, so the wait needs to say which stage is running, what it's doing,
 * and roughly how long — a disabled button reads as a hung page.
 */
const RunInProgress = ({
  run,
  itemNoun,
  canWrite,
  onStop,
  stopping,
}: {
  run: GenerationRun;
  itemNoun: string;
  canWrite: boolean;
  onStop: () => void;
  stopping: boolean;
}) => {
  const copy = runCopy(run, itemNoun);
  return (
    <div className="rounded-lg border border-outline bg-surface-elevated p-6">
      {/* The DS's own live-state affordance, rather than a hand-rolled spinner. */}
      <StatusIndicator tone="info" pulse>
        {copy.heading}
      </StatusIndicator>
      <p className="mt-2 text-sm text-ink-muted">{copy.detail}</p>
      {run.progress && <RunProgressBar done={run.progress.done} total={run.progress.total} />}
      <p className="mt-2 text-sm text-ink-subtle">
        <RunStatusLine run={run} />
        {' · '}
        {copy.eta}
      </p>
      {canWrite && (
        <Button variant="ghost" className="mt-3" onClick={onStop} disabled={stopping}>
          {stopping ? 'Stopping…' : 'Stop'}
        </Button>
      )}
    </div>
  );
};

/**
 * The same run, reported above the panes once guides exist — writing takes
 * minutes and the reviewer is reading the finished ones meanwhile, so the work
 * has to stay visible without taking the page over.
 */
const RunProgressStrip = ({
  run,
  itemNoun,
  canWrite,
  onStop,
  stopping,
}: {
  run: GenerationRun;
  itemNoun: string;
  canWrite: boolean;
  onStop: () => void;
  stopping: boolean;
}) => {
  const copy = runCopy(run, itemNoun);
  return (
    <div className="rounded-lg border border-outline bg-surface-elevated px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <StatusIndicator tone="info" pulse>
          {copy.heading}
        </StatusIndicator>
        <span className="ml-auto text-sm text-ink-muted">
          <RunStatusLine run={run} />
        </span>
        {canWrite && (
          <Button size="sm" variant="ghost" onClick={onStop} disabled={stopping}>
            {stopping ? 'Stopping…' : 'Stop'}
          </Button>
        )}
      </div>
      {run.progress && <RunProgressBar done={run.progress.done} total={run.progress.total} />}
    </div>
  );
};

export const GuideViewerPage = () => {
  const { subjectId = '', itemId } = useParams();
  const navigate = useNavigate();

  const me = useMe();
  const canWrite = me.data ? canWriteKnowledge(me.data.user) : false;
  const qc = useQueryClient();

  const subject = useQuery({
    queryKey: ['knowledge', 'subject', subjectId],
    queryFn: () => knowledgeApi.subject(subjectId),
    enabled: subjectId.length > 0,
  });
  // Runs are read before items so the items query can poll off an in-flight
  // run: keying that off "some item is GENERATING" alone left the guides stale
  // between items, so bodies lagged the progress the strip was reporting.
  const runs = useQuery({
    queryKey: ['knowledge', 'runs', subjectId],
    queryFn: () => knowledgeApi.runs(subjectId),
    refetchInterval: (q) =>
      (q.state.data ?? []).some((r) => r.status === 'RUNNING' || r.status === 'PENDING')
        ? 5000
        : false,
  });
  const activeRun = (runs.data ?? []).find((r) => r.status === 'RUNNING' || r.status === 'PENDING');
  const runInFlight = activeRun !== undefined;

  const items = useQuery({
    queryKey: ['knowledge', 'items', subjectId],
    queryFn: () => knowledgeApi.items(subjectId),
    enabled: subjectId.length > 0,
    refetchInterval: (q) =>
      runInFlight || (q.state.data ?? []).some((i) => i.status === 'GENERATING') ? 5000 : false,
  });

  const visible = (items.data ?? []).filter((i) => i.status !== 'ARCHIVED');
  const current = visible.find((i) => i.id === itemId) ?? visible[0];
  // Canonicalize the bare /review URL to the guide it's actually showing.
  // Without this, the default view renders the first failure mode while the
  // address bar stays anonymous — so "look at this one" copies a link that
  // doesn't say which one, and the same page has two names. `replace` keeps
  // Back from bouncing through the id-less form. An unknown :itemId also
  // lands here (current falls back to the first guide), which heals stale
  // links to deleted guides instead of 404ing them.
  useEffect(() => {
    if (current && current.id !== itemId) {
      void navigate(`/knowledge/subjects/${subjectId}/review/${current.id}`, { replace: true });
    }
  }, [current, itemId, subjectId, navigate]);
  // Only the LATEST run's failure is worth reporting: matching any errored run
  // meant an old failure kept accusing a subject that has since succeeded.
  const latestRun = [...(runs.data ?? [])].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  )[0];
  const failedRun = latestRun?.status === 'ERROR' ? latestRun : undefined;
  const curatable = (runs.data ?? []).find(
    (r) => r.kind === 'LIST' && r.status === 'COMPLETE' && (r.listOutput?.titles?.length ?? 0) > 0,
  );

  // Editing state — draft text lives locally until Save.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [confirm, setConfirm] = useState<'regenerate' | 'delete' | 'writeMissing' | 'reset' | null>(
    null,
  );
  const [tab, setTab] = useState<'guides' | 'details'>('guides');
  const [pane, setPane] = useState<'guide' | 'history'>('guide');
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  // Selecting a different guide leaves edit mode and returns to reading it
  // rather than staying on the previous guide's history.
  //
  // Adjusted DURING render, not in an effect: an effect runs after paint, so
  // there was one frame showing the newly-selected guide in the editor with
  // the previous guide's draft text in it. This is React's documented pattern
  // for resetting state when a value changes, and it needs no external system
  // to synchronize with — which is what an effect would have been for.
  const [lastGuideId, setLastGuideId] = useState(current?.id);
  if (current?.id !== lastGuideId) {
    setLastGuideId(current?.id);
    setEditing(false);
    setPane('guide');
  }

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['knowledge'] });
  const save = useMutation({
    mutationFn: () => {
      if (!current) throw new Error('no guide selected');
      return knowledgeApi.updateItem(current.id, { bodyMarkdown: draft });
    },
    onSuccess: () => {
      setEditing(false);
      invalidate();
    },
  });
  const approve = useMutation({
    mutationFn: () => {
      if (!current) throw new Error('no guide selected');
      return knowledgeApi.approveItem(current.id);
    },
    onSuccess: invalidate,
  });
  const regenerate = useMutation({
    mutationFn: () => {
      if (!current) throw new Error('no guide selected');
      return knowledgeApi.enqueueRun({ subjectId, kind: 'ITEM', itemId: current.id });
    },
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });
  const enqueue = useMutation({
    mutationFn: (kind: 'LIST' | 'ITEMS') => knowledgeApi.enqueueRun({ subjectId, kind }),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });
  const materialize = useMutation({
    mutationFn: (titles: string[]) => {
      if (!curatable) throw new Error('no suggested list to save');
      return knowledgeApi.materializeRun(curatable.id, titles);
    },
    onSuccess: invalidate,
  });
  const addItem = useMutation({
    mutationFn: () => knowledgeApi.createItem(subjectId, { title: newTitle.trim() }),
    onSuccess: (created) => {
      setAdding(false);
      setNewTitle('');
      invalidate();
      void navigate(`/knowledge/subjects/${subjectId}/review/${created.id}`);
    },
  });
  const reset = useMutation({
    mutationFn: () => knowledgeApi.resetSubject(subjectId),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });
  const cancelRun = useMutation({
    mutationFn: (runId: string) => knowledgeApi.cancelRun(runId),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => {
      if (!current) throw new Error('no guide selected');
      return knowledgeApi.deleteItem(current.id);
    },
    onSuccess: () => {
      setConfirm(null);
      invalidate();
      // The deleted guide was the selected one; fall back to the list.
      void navigate(`/knowledge/subjects/${subjectId}/review`);
    },
  });

  if (subject.isLoading || items.isLoading) return <Skeleton className="h-64" />;
  if (subject.error || !subject.data) {
    return <div className="text-sm text-danger">Couldn’t load this subject.</div>;
  }
  const s = subject.data;
  const mutationError =
    save.error ?? approve.error ?? regenerate.error ?? remove.error ?? enqueue.error;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <section>
        <Breadcrumbs
          trail={[
            { label: 'Categories', to: '/knowledge/categories' },
            { label: s.templateName, to: `/knowledge/categories/${s.templateId}` },
          ]}
        />
        {/* Display only — all subject editing lives in the Subject details tab. */}
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-bold text-ink">{s.name}</h2>
          {/* The category is already in the breadcrumb directly above. */}
          {s.kind && <Badge variant="neutral">{s.kind}</Badge>}
        </div>
        {s.notes && <p className="mt-1 max-w-3xl text-sm text-ink-muted">{s.notes}</p>}
      </section>

      <div className="flex gap-1 border-b border-outline" role="tablist">
        {(
          [
            { id: 'guides', label: `${s.itemNoun}s` },
            { id: 'details', label: 'Subject details' },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.id
                ? 'border-primary text-ink'
                : 'border-transparent text-ink-muted hover:text-ink'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {mutationError && (
        <Banner variant="danger">
          <Banner.Description>{(mutationError as Error).message}</Banner.Description>
        </Banner>
      )}
      {!activeRun && failedRun?.errorMessage && (
        <Banner variant="warning">
          <Banner.Title>The last run didn’t finish</Banner.Title>
          <Banner.Description>{failedRun.errorMessage}</Banner.Description>
        </Banner>
      )}

      {tab === 'details' && (
        <SubjectDetailsTab
          key={s.id}
          subject={s}
          onStartOver={visible.length > 0 ? () => setConfirm('reset') : undefined}
        />
      )}

      {/* Reported here, above both panes, because the reviewer reads the guides
          that are already done while the rest are still being written. */}
      {tab === 'guides' && activeRun && visible.length > 0 && (
        <RunProgressStrip
          run={activeRun}
          itemNoun={s.itemNoun}
          canWrite={canWrite}
          onStop={() => cancelRun.mutate(activeRun.id)}
          stopping={cancelRun.isPending}
        />
      )}

      <div className={tab === 'guides' ? 'flex min-h-0 flex-1 items-start gap-4' : 'hidden'}>
        {/* ── the guide list ── */}
        <aside className="w-80 shrink-0 overflow-hidden rounded-lg border border-outline bg-surface-elevated">
          <div className="flex items-center justify-between gap-2 border-b border-outline px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
              {s.itemNoun}s ({visible.length})
            </span>
            {/* Stopping a run belongs with the run's own progress, not beside
                the list header where it read as "stop the list". */}
            {canWrite && (
              <Button size="sm" variant="ghost" onClick={() => setAdding(true)}>
                + Add
              </Button>
            )}
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {visible.map((i) => (
              <GuideListEntry
                key={i.id}
                item={i}
                selected={i.id === current?.id}
                onSelect={() => void navigate(`/knowledge/subjects/${subjectId}/review/${i.id}`)}
              />
            ))}
            {visible.length === 0 && (
              <div className="p-4 text-sm text-ink-subtle">
                Nothing here yet.
                {canWrite && !curatable && !runInFlight && ' Use the button on the right to start.'}
              </div>
            )}
          </div>
        </aside>

        {/* ── the guide ── */}
        <section className="min-w-0 flex-1">
          {current ? (
            <>
              <div className="mb-3 flex gap-1 border-b border-outline" role="tablist">
                {(
                  [
                    { id: 'guide', label: 'Guide' },
                    { id: 'history', label: 'History' },
                  ] as const
                ).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={pane === t.id}
                    onClick={() => setPane(t.id)}
                    className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                      pane === t.id
                        ? 'border-primary text-ink'
                        : 'border-transparent text-ink-muted hover:text-ink'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {pane === 'history' ? (
                <GuideHistoryPanel
                  itemId={current.id}
                  currentVersion={current.version}
                  onRestored={() => setPane('guide')}
                />
              ) : (
                <>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    {current.status === 'PUBLISHED' && current.hasUnpublishedChanges ? (
                      <Badge variant="info">edited</Badge>
                    ) : (
                      <Badge variant={STATUS_LABEL[current.status].variant}>
                        {STATUS_LABEL[current.status].text}
                      </Badge>
                    )}
                    <div className="ml-auto flex flex-wrap gap-2">
                      {canWrite && !editing && current.status !== 'GENERATING' && (
                        <>
                          <Button
                            variant="secondary"
                            onClick={() => {
                              setDraft(current.bodyMarkdown);
                              setEditing(true);
                            }}
                          >
                            Edit
                          </Button>
                          <Button variant="secondary" onClick={() => setConfirm('regenerate')}>
                            Regenerate
                          </Button>
                          <Button variant="ghost" onClick={() => setConfirm('delete')}>
                            Delete
                          </Button>
                          {(current.status === 'DRAFT' ||
                            (current.status === 'PUBLISHED' && current.hasUnpublishedChanges)) && (
                            <Button onClick={() => approve.mutate()} disabled={approve.isPending}>
                              {approve.isPending ? 'Publishing…' : 'Approve & publish'}
                            </Button>
                          )}
                        </>
                      )}
                      {canWrite && editing && (
                        <>
                          <Button variant="ghost" onClick={() => setEditing(false)}>
                            Cancel
                          </Button>
                          <Button onClick={() => save.mutate()} disabled={save.isPending}>
                            {save.isPending ? 'Saving…' : 'Save changes'}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {current.status === 'GENERATION_FAILED' && current.errorMessage && (
                    <Banner variant="warning" className="mb-3">
                      <Banner.Title>
                        The last attempt to write this guide didn’t finish
                      </Banner.Title>
                      <Banner.Description>
                        {/* errorMessage is a full sentence now (platform #369),
                            so the hint stands as its own sentence rather than a
                            dash-spliced clause. */}
                        {current.errorMessage} Regenerate to try again, or Edit to write it by hand.
                      </Banner.Description>
                    </Banner>
                  )}

                  {editing ? (
                    <RichTextEditor value={draft} onChange={setDraft} />
                  ) : (
                    <div className="rounded-lg border border-outline bg-surface-elevated p-6">
                      {current.status === 'GENERATING' ? (
                        <StatusIndicator tone="info" pulse>
                          Being written right now — it will appear here when it’s done
                        </StatusIndicator>
                      ) : current.bodyMarkdown.trim().length === 0 ? (
                        <p className="text-sm text-ink-subtle">
                          {runInFlight
                            ? 'Waiting its turn — the writer is working down the list.'
                            : `Not written yet. Regenerate to have it written, or Edit to write it yourself.`}
                        </p>
                      ) : (
                        <MarkdownViewer>{current.bodyMarkdown}</MarkdownViewer>
                      )}
                    </div>
                  )}
                </>
              )}
            </>
          ) : curatable && canWrite ? (
            <CurateList
              suggested={curatable.listOutput?.titles ?? []}
              onSave={(titles) => materialize.mutate(titles)}
              saving={materialize.isPending}
              error={(materialize.error as Error | null) ?? null}
              itemNoun={s.itemNoun}
            />
          ) : activeRun ? (
            <RunInProgress
              run={activeRun}
              itemNoun={s.itemNoun}
              canWrite={canWrite}
              onStop={() => cancelRun.mutate(activeRun.id)}
              stopping={cancelRun.isPending}
            />
          ) : (
            <div className="rounded-lg border border-outline bg-surface-elevated p-6">
              {canWrite ? (
                <>
                  <p className="mb-3 text-sm text-ink-muted">
                    {s.listMode === 'MANUAL'
                      ? `This category writes its ${s.itemNoun.toLowerCase()} list by hand — add the first one to get started.`
                      : `Have a ${s.itemNoun.toLowerCase()} list suggested from what the system knows — you review and edit it before any guides are written — or write the first one yourself.`}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {/* A MANUAL list has nothing to generate, so don't lead
                        with a button whose only outcome is an error. */}
                    {s.listMode === 'MANUAL' ? (
                      <Button onClick={() => setAdding(true)}>
                        Add a {s.itemNoun.toLowerCase()}
                      </Button>
                    ) : (
                      <>
                        <Button
                          onClick={() => enqueue.mutate('LIST')}
                          disabled={enqueue.isPending || runInFlight}
                        >
                          {runInFlight
                            ? 'Working on it…'
                            : `Suggest a ${s.itemNoun.toLowerCase()} list`}
                        </Button>
                        <Button variant="secondary" onClick={() => setAdding(true)}>
                          Add one by hand
                        </Button>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm text-ink-subtle">Nothing to review yet.</p>
              )}
            </div>
          )}
        </section>
      </div>

      {/* Plain-words confirmations. House convention (see GroupsPage): the
          modal MOUNTS only while open — the DS Modal paints its children even
          when open={false}, so mounted-but-closed renders always-open. */}
      {confirm === 'regenerate' && (
        <Modal open onOpenChange={(open) => !open && setConfirm(null)}>
          <Modal.Header>
            <Modal.Title>Rewrite this guide?</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <p className="text-sm text-ink-muted">
              A fresh version will be written and saved as a draft for your review. The current
              version stays in the history, and nothing goes live until you approve it.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              Keep the current version
            </Button>
            <Button onClick={() => regenerate.mutate()} disabled={regenerate.isPending}>
              {regenerate.isPending ? 'Starting…' : 'Rewrite it'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {adding && (
        <Modal
          open
          onOpenChange={(open) => {
            if (!open) setAdding(false);
          }}
        >
          <Modal.Header>
            <Modal.Title>Add a {s.itemNoun.toLowerCase()}</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <label className="block text-sm font-medium text-ink">
              What is it called?
              <span className="mt-0.5 block text-xs font-normal text-ink-muted">
                Name it the way someone in the field would say it.
              </span>
              <Input
                value={newTitle}
                aria-label={`${s.itemNoun} name`}
                onChange={(e) => setNewTitle(e.target.value)}
                className="mt-1 w-full"
              />
            </label>
            {addItem.error && (
              <p className="mt-3 text-sm text-danger">{(addItem.error as Error).message}</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => addItem.mutate()}
              disabled={addItem.isPending || newTitle.trim().length === 0}
            >
              {addItem.isPending ? 'Adding…' : 'Add'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {confirm === 'reset' && (
        <Modal open onOpenChange={(open) => !open && setConfirm(null)}>
          <Modal.Header>
            <Modal.Title>Start {s.name} over?</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <p className="text-sm text-ink-muted">
              Every {s.itemNoun.toLowerCase()} goes back to draft and stops being used in answers,
              and the run history is cleared. The {s.itemNoun.toLowerCase()}s themselves and your
              uploaded documents are kept — you can review, edit or regenerate them from here.
            </p>
            {reset.error && (
              <p className="mt-3 text-sm text-danger">{(reset.error as Error).message}</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              Leave it as it is
            </Button>
            <Button variant="danger" onClick={() => reset.mutate()} disabled={reset.isPending}>
              {reset.isPending ? 'Starting over…' : 'Start over'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {confirm === 'delete' && (
        <Modal open onOpenChange={(open) => !open && setConfirm(null)}>
          <Modal.Header>
            <Modal.Title>Delete this {s.itemNoun.toLowerCase()}?</Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <p className="text-sm text-ink-muted">
              It stops being used in answers straight away, and its edit history goes with it. This
              can’t be undone.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              Keep it
            </Button>
            <Button variant="danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};
